// QA page block: every ban across all apps, newest first. The per-app view
// lives in the app card (components/AppBanPanel); this is the cross-app list
// the ban statistics are read from.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { formatDateTime } from '@/lib/datetime';
import { getAppBans, type AppBanState } from '@/api/app-bans';

const FILTERS: { value: AppBanState; label: string }[] = [
  { value: 'all', label: 'Все баны' },
  { value: 'open', label: 'Активные' },
  { value: 'cleared', label: 'Снятые' },
];

export function AppBans() {
  const qc = useQueryClient();
  const [state, setState] = useState<AppBanState>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['app-bans-all', state],
    queryFn: () => getAppBans({ state, limit: 200 }),
  });

  const bans = data?.bans ?? [];
  const summary = data?.summary;

  return (
    <div className="bg-surface border border-border rounded-lg mt-6">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-content">Баны приложений</h2>
        <p className="text-sm text-content-subtle mt-1">
          Что случилось с прилой, кто это записал и чем подтверждено. Отметить бан
          и приложить скриншоты можно в карточке приложения на странице Apps.
        </p>
      </div>

      <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-4">
        <div className="w-56">
          <Select
            value={state}
            options={FILTERS}
            onChange={(v) => setState(v as AppBanState)}
            size="sm"
          />
        </div>
        {summary && (
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-content-subtle">Всего: <span className="text-content">{summary.total}</span></span>
            <span className="text-content-subtle">Активных: <span className="text-danger">{summary.open}</span></span>
            <span className="text-content-subtle">Снятых: <span className="text-content">{summary.cleared}</span></span>
            <span className="text-content-subtle">Вручную: <span className="text-content">{summary.manual}</span></span>
            <span className="text-content-subtle">Прил задето: <span className="text-content">{summary.apps}</span></span>
          </div>
        )}
        <button
          type="button"
          onClick={() => qc.invalidateQueries({ queryKey: ['app-bans-all'] })}
          className="text-xs text-primary hover:underline"
        >
          Обновить
        </button>
      </div>

      {isLoading ? (
        <p className="px-6 py-5 text-sm text-content-subtle">Загрузка…</p>
      ) : bans.length === 0 ? (
        <p className="px-6 py-5 text-sm text-content-subtle">Банов нет.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle border-b border-border">
                <th className="px-6 py-2 font-medium">Приложение</th>
                <th className="px-6 py-2 font-medium">Дата бана</th>
                <th className="px-6 py-2 font-medium">Источник</th>
                <th className="px-6 py-2 font-medium">Описание</th>
                <th className="px-6 py-2 font-medium">Скрины</th>
                <th className="px-6 py-2 font-medium">Состояние</th>
              </tr>
            </thead>
            <tbody>
              {bans.map((ban) => (
                <tr key={ban.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-6 py-3">
                    <div className="text-content">{ban.app_name || ban.app_id}</div>
                    {ban.bundle_id && <div className="text-xs text-content-subtle font-mono">{ban.bundle_id}</div>}
                  </td>
                  <td className="px-6 py-3 text-content-muted whitespace-nowrap">{formatDateTime(ban.detected_at)}</td>
                  <td className="px-6 py-3 text-content-muted whitespace-nowrap">
                    {ban.source === 'auto' ? 'Автодетект' : (ban.created_by_name || ban.created_by_email || 'Тестер')}
                  </td>
                  <td className="px-6 py-3 text-content max-w-md whitespace-pre-wrap">{ban.reason}</td>
                  <td className="px-6 py-3 text-content-muted">{ban.screenshot_count || '—'}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {ban.cleared_at ? (
                      <span className="text-content-subtle" title={ban.cleared_note || undefined}>
                        Снят {formatDateTime(ban.cleared_at)}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-danger text-white text-xs">Активен</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
